// @ts-check

/**
 * @file Provides the shared React Native button component.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {Link} from "expo-router"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Animated} from "react-native"
import {Animated as PropForgeAnimated, Pressable, View} from "react-native-propforge"

import {FORM_CONTROL_BORDER_RADIUS, FORM_CONTROL_BORDER_WIDTH, FORM_CONTROL_FONT_SIZE, FORM_CONTROL_LINE_HEIGHT, FORM_CONTROL_PADDING_VERTICAL} from "../shared/form-control-metrics"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {BoardThemeColors} from "../shared/theme" */

/** @typedef {"primary" | "secondary" | "destructive" | "destructive-outline" | "neutral" | "outline"} ButtonVariant */
/** @typedef {import("react").ComponentProps<typeof FontAwesome6>["name"]} FontAwesome6Name */

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

const ICON_LABEL_GAP = 8
const BUTTON_COLOR_TRANSITION_MS = 180

/**
 * Resolves colors for a button variant from the active palette.
 * @param {ButtonVariant} variant Button variant.
 * @param {boolean} disabled Whether the button is disabled.
 * @param {BoardThemeColors} colors Active theme palette.
 * @returns {{backgroundColor: string, textColor: string, borderColor: string}} Button colors.
 */
function variantStyles(variant, disabled, colors) {
  if (disabled) {
    return {
      backgroundColor: colors.disabledBackground,
      borderColor: colors.disabledBackground,
      textColor: colors.disabledText
    }
  }
  if (variant === "destructive")
    return {
      backgroundColor: colors.danger,
      borderColor: colors.danger,
      textColor: colors.onPrimary
    }
  if (variant === "destructive-outline")
    return {
      backgroundColor: colors.dangerBackground,
      borderColor: colors.danger,
      textColor: colors.dangerText
    }
  if (variant === "neutral")
    return {
      backgroundColor: colors.text,
      borderColor: colors.text,
      textColor: colors.textInverse
    }
  if (variant === "secondary")
    return {
      backgroundColor: colors.secondary,
      borderColor: colors.secondary,
      textColor: colors.onSecondary
    }
  if (variant === "outline")
    return {
      backgroundColor: colors.infoBackground,
      borderColor: colors.info,
      textColor: colors.link
    }
  return {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    textColor: colors.onPrimary
  }
}

/**
 * @typedef {object} ButtonProps
 * @property {boolean} [disabled] Whether the button is disabled. Ignored when `href` is set.
 * @property {boolean} [fullWidth] Whether the button should fill its parent width.
 * @property {string} [href] Render the button as an `expo-router` Link to this path. Mutually exclusive with `onPress`.
 * @property {FontAwesome6Name} [iconName] Optional FontAwesome6 icon shown left of the label.
 * @property {string} label Button label.
 * @property {(...args: unknown[]) => unknown} [onPress] Press callback. Ignored when `href` is set.
 * @property {string} testID Stable test id.
 * @property {ButtonVariant} [variant] Visual variant.
 */

/** @typedef {Record<string, never>} ButtonState */

/**
 * Reusable Button used across the app for both press handlers and
 * navigation links — pass `onPress` for a Pressable, or `href` to render
 * as an `expo-router` Link so middle-click / cmd-click "open in new
 * tab" works natively. Either way the icon + label markup, padding,
 * radius, and gap stay consistent.
 * @augments {ShapeComponent<ButtonProps, ButtonState>}
 */
class Button extends ShapeComponent {
  static propTypes = propTypesExact({
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool,
    href: PropTypes.string,
    iconName: PropTypes.string,
    label: PropTypes.string.isRequired,
    onPress: PropTypes.func,
    testID: PropTypes.string.isRequired,
    variant: PropTypes.oneOf([
      "primary",
      "secondary",
      "destructive",
      "destructive-outline",
      "neutral",
      "outline"
    ])
  })
  static defaultProps = {
    disabled: false,
    fullWidth: false,
    href: undefined,
    iconName: undefined,
    onPress: undefined,
    variant: "primary"
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {Animated.Value} */
  disabledAnimation = new Animated.Value(0)

  /** @type {Record<string, object>} */
  animatedContainerStyles = {}

  /** @type {Record<string, object[]>} */
  animatedContainerStyleArrays = {}

  /** @type {Record<string, object>} */
  animatedTextStyles = {}

  /** @type {Record<string, object[]>} */
  animatedTextStyleArrays = {}

  /** @type {boolean} */
  disabledAnimationPrimed = false

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const disabled = this.tt.effectiveDisabled()
    if (!this.disabledAnimationPrimed) {
      this.disabledAnimationPrimed = true
      this.tt.disabledAnimation.setValue(disabled ? 1 : 0)
    }
    useEffect(() => {
      Animated.timing(this.tt.disabledAnimation, {
        duration: BUTTON_COLOR_TRANSITION_MS,
        toValue: disabled ? 1 : 0,
        useNativeDriver: false
      }).start()
    }, [disabled])
  }

  /**
   * Renders the shared button.
   * @returns {ReactNode} Button content.
   */
  render() {
    const {fullWidth, href, iconName, label, onPress, testID, variant} = this.p
    const enabledColors = variantStyles(
      /** @type {ButtonVariant} */ variant,
      false,
      this.tt.colors
    )
    const disabledColors = variantStyles(
      /** @type {ButtonVariant} */ variant,
      true,
      this.tt.colors
    )
    const effectiveDisabled = this.tt.effectiveDisabled()
    const iconColor = effectiveDisabled ? disabledColors.textColor : enabledColors.textColor
    const containerStyle = styles[`buttonContainer-${fullWidth}`] ||= {
      borderRadius: FORM_CONTROL_BORDER_RADIUS,
      borderWidth: FORM_CONTROL_BORDER_WIDTH,
      overflow: "hidden",
      width: fullWidth ? "100%" : undefined
    }
    const pressableContentStyle = styles[`buttonPressableContent-${fullWidth}`] ||= {
      alignItems: "center",
      flexDirection: "row",
      paddingHorizontal: 14,
      paddingVertical: FORM_CONTROL_PADDING_VERTICAL,
      width: fullWidth ? "100%" : undefined
    }
    const iconStyle = styles.buttonIcon ||= {
      alignItems: "center",
      justifyContent: "center",
      marginRight: ICON_LABEL_GAP,
      width: 16
    }
    const textStyle = styles.buttonText ||= {
      fontSize: FORM_CONTROL_FONT_SIZE,
      fontWeight: 700,
      lineHeight: FORM_CONTROL_LINE_HEIGHT
    }
    if (href) {
      const linkContainerStyle = styles[`buttonLinkContainer-${this.tt.themeName}-${variant}-${fullWidth}`] ||= {
        alignItems: "center",
        backgroundColor: enabledColors.backgroundColor,
        borderColor: enabledColors.borderColor,
        borderRadius: FORM_CONTROL_BORDER_RADIUS,
        borderWidth: FORM_CONTROL_BORDER_WIDTH,
        flexDirection: "row",
        paddingHorizontal: 14,
        paddingVertical: FORM_CONTROL_PADDING_VERTICAL,
        width: fullWidth ? "100%" : undefined
      }
      const linkTextStyle = styles[`buttonLinkText-${this.tt.themeName}-${variant}`] ||= {
        color: enabledColors.textColor,
        fontSize: FORM_CONTROL_FONT_SIZE,
        fontWeight: 700,
        lineHeight: FORM_CONTROL_LINE_HEIGHT
      }
      return (
        <Link href={href} style={linkContainerStyle} testID={testID}>
          {iconName &&
            <View
              style={iconStyle}
              testID={`${testID}/icon`}
            >
              <FontAwesome6 color={enabledColors.textColor} name={iconName} size={14} />
            </View>
          }
          <PropForgeAnimated.Text style={linkTextStyle} testID={`${testID}/label`}>
            {label}
          </PropForgeAnimated.Text>
        </Link>
      )
    }
    const animatedContainerStyle = this.animatedContainerStyles[`buttonContainerColors-${this.tt.themeName}-${variant}`] ||= {
      backgroundColor: this.tt.disabledAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [enabledColors.backgroundColor, disabledColors.backgroundColor]
      }),
      borderColor: this.tt.disabledAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [enabledColors.borderColor, disabledColors.borderColor]
      })
    }
    const animatedContainerStyles = this.animatedContainerStyleArrays[`buttonContainerStyle-${this.tt.themeName}-${variant}-${fullWidth}`] ||= [
      containerStyle,
      animatedContainerStyle
    ]
    const animatedTextStyle = this.animatedTextStyles[`buttonTextColors-${this.tt.themeName}-${variant}`] ||= {
      color: this.tt.disabledAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [enabledColors.textColor, disabledColors.textColor]
      })
    }
    const animatedTextStyles = this.animatedTextStyleArrays[`buttonTextStyle-${this.tt.themeName}-${variant}`] ||= [
      textStyle,
      animatedTextStyle
    ]
    return (
      <PropForgeAnimated.View style={animatedContainerStyles}>
        <Pressable
          disabled={effectiveDisabled}
          onPress={onPress}
          style={pressableContentStyle}
          testID={testID}
        >
          {iconName &&
            <View
              style={iconStyle}
              testID={`${testID}/icon`}
            >
              <FontAwesome6 color={iconColor} name={iconName} size={14} />
            </View>
          }
          <PropForgeAnimated.Text style={animatedTextStyles} testID={`${testID}/label`}>
            {label}
          </PropForgeAnimated.Text>
        </Pressable>
      </PropForgeAnimated.View>
    )
  }

  /**
   * Resolves whether the button should behave as disabled.
   * @returns {boolean} True when the pressable button is disabled.
   */
  effectiveDisabled() {
    return !this.p.href && Boolean(this.p.disabled)
  }
}

/** @type {NamedExoticComponent<ButtonProps>} */
const ButtonComponent = memo(shapeComponent(Button))

export default ButtonComponent
